import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useScreen } from '../../../hooks'
import { Widget } from '../Widget'

import { getCompanyContactsApi } from './ProfileContactsWidget.api'
import {
  useProfileContactsWidgetCreateForm,
  useProfileContactsWidgetLoader,
} from './ProfileContactsWidget.hooks'
import {
  getExistingTypes,
  getIsEmpty,
  getIsLimitReached,
} from './ProfileContactsWidget.utils'
import { ProfileContactsWidgetButtonSelect } from './ProfileContactsWidgetButtonSelect'
import { ProfileContactsWidgetCreateForm } from './ProfileContactsWidgetCreateForm'
import { ProfileContactsWidgetEmpty } from './ProfileContactsWidgetEmpty'
import { ProfileContactsWidgetList } from './ProfileContactsWidgetList'
import { PROFILE_CONTACTS_WIDGET_VALUE_FIELD } from './ProfileContactsWidgetListCell'
import { ProfileContactsWidgetSkeleton } from './ProfileContactsWidgetSkeleton'

import * as styles from './ProfileContactsWidget.module.css'

function ProfileContactsWidget({
  api,
  canEdit,
  entityId,
  model,
  onClickEmail,
  onHideWidget,
  onLoaded,
}) {
  const { t } = useTranslation()

  const { isMobileScreenSize } = useScreen()

  const contactsApi = api ?? getCompanyContactsApi({ entityId, model })

  const [editingContactId, setEditingContactId] = useState(null)
  const [editingIsDirty, setEditingIsDirty] = useState(false)

  const {
    addTypes,
    canCreate,
    contacts,
    fetchContacts,
    isLoaded,
    limit,
    updateContactValue,
  } = useProfileContactsWidgetLoader({
    api: contactsApi,
    canEdit,
    onHideWidget,
    onLoaded,
  })

  const {
    handleCancelCreate,
    handleCreateSubmit,
    handleRemoveContact,
    handleSelectItem,
    initialValues,
    newContacts,
  } = useProfileContactsWidgetCreateForm({
    api: contactsApi,
    onCreated: fetchContacts,
  })

  function handleEditRequest(contactId) {
    setEditingContactId(contactId)
    setEditingIsDirty(false)
  }

  function handleDirtyChange(isDirty) {
    setEditingIsDirty(isDirty)
  }

  const isEmpty = getIsEmpty({ contacts, newContacts })

  const existingTypes = getExistingTypes({ contacts, newContacts })

  const isLimitReached = getIsLimitReached({ contacts, limit, newContacts })

  return !isLoaded ? (
    <ProfileContactsWidgetSkeleton />
  ) : isEmpty ? (
    <ProfileContactsWidgetEmpty
      addTypes={addTypes}
      canEdit={canEdit && canCreate}
      existingTypes={existingTypes}
      onSelectItem={handleSelectItem}
    />
  ) : (
    <Widget
      contentContainerProps={{ className: styles.content }}
      headerProps={{ contentProps: { actionsProps: { EditButton: null } } }}
      initiallyExpanded={!isMobileScreenSize}
      title={t('ProfileContactsWidget.TitleKey')}
    >
      <ProfileContactsWidgetList
        api={contactsApi}
        canEdit={canEdit}
        contacts={contacts}
        editingContactId={editingContactId}
        editingIsDirty={editingIsDirty}
        onClickEmail={onClickEmail}
        onDirtyChange={handleDirtyChange}
        onEdited={updateContactValue}
        onEditRequest={handleEditRequest}
        onSuccess={fetchContacts}
      />
      {canEdit && canCreate ? (
        newContacts.length > 0 ? (
          <ProfileContactsWidgetCreateForm
            addTypes={addTypes}
            existingTypes={existingTypes}
            initialValues={initialValues}
            isLimitReached={isLimitReached}
            name={PROFILE_CONTACTS_WIDGET_VALUE_FIELD}
            newContacts={newContacts}
            onClick={handleCancelCreate}
            onRemoveContact={handleRemoveContact}
            onSelectItem={handleSelectItem}
            onSubmit={handleCreateSubmit}
          />
        ) : (
          <ProfileContactsWidgetButtonSelect
            addTypes={addTypes}
            disabled={isLimitReached}
            existingTypes={existingTypes}
            isLimitReached={isLimitReached}
            onSelectItem={handleSelectItem}
          />
        )
      ) : null}
    </Widget>
  )
}

export default ProfileContactsWidget
